import { useState, useEffect } from "react";
import { API, useAuth } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  TrendingUp,
  TrendingDown,
  Target,
  BarChart3,
  Activity,
  Users,
  Minus,
  ArrowUpRight,
  ArrowDownRight
} from "lucide-react";

export default function Analytics() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("month");

  const isTeamViewer = user?.is_manager || user?.is_admin;

  useEffect(() => {
    const fetchAnalytics = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API}/analytics?period=${period}`, { credentials: "include" });
        if (res.ok) setData(await res.json());
      } catch (error) {
        toast.error("Failed to load analytics");
      } finally {
        setLoading(false);
      }
    };
    fetchAnalytics();
  }, [period]);

  const getScoreColor = (score) => {
    if (score >= 80) return "text-green-500";
    if (score >= 60) return "text-yellow-500";
    return "text-red-500";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const personal = data?.personal;
  const team = data?.team;

  return (
    <div className="max-w-6xl mx-auto space-y-8" data-testid="analytics-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold">Analytics</h1>
          <p className="text-muted-foreground mt-1">Track your progress and performance</p>
        </div>
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="w-44" data-testid="analytics-period-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="week">This Week</SelectItem>
            <SelectItem value="month">This Month</SelectItem>
            <SelectItem value="quarter">This Quarter</SelectItem>
            <SelectItem value="all">All Time</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {isTeamViewer && team ? (
        <Tabs defaultValue="personal" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="personal" data-testid="personal-tab">My Stats</TabsTrigger>
            <TabsTrigger value="team" data-testid="team-tab">Team Stats</TabsTrigger>
          </TabsList>
          <TabsContent value="personal" className="mt-6 space-y-6">
            <PersonalStats personal={personal} getScoreColor={getScoreColor} />
          </TabsContent>
          <TabsContent value="team" className="mt-6 space-y-6">
            <TeamStats team={team} getScoreColor={getScoreColor} />
          </TabsContent>
        </Tabs>
      ) : (
        <PersonalStats personal={personal} getScoreColor={getScoreColor} />
      )}
    </div>
  );
}

function PersonalStats({ personal, getScoreColor }) {
  if (!personal) return null;

  const maxTrendSessions = Math.max(...(personal.score_trend?.map(d => d.sessions) || [1]), 1);
  const maxTrendScore = Math.max(...(personal.score_trend?.map(d => d.avg_score) || [1]), 1);

  return (
    <>
      {/* KPI Cards */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card data-testid="total-sessions-card">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-muted-foreground">Total Sessions</span>
              <Activity className="w-4 h-4 text-muted-foreground" />
            </div>
            <p className="font-heading text-3xl font-bold">{personal.total_sessions}</p>
          </CardContent>
        </Card>

        <Card data-testid="avg-score-card">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-muted-foreground">Avg Score</span>
              <Target className="w-4 h-4 text-muted-foreground" />
            </div>
            <p className={`font-heading text-3xl font-bold ${getScoreColor(personal.avg_score)}`}>
              {personal.avg_score}
            </p>
          </CardContent>
        </Card>

        <Card data-testid="best-score-card">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-muted-foreground">Best Score</span>
              <TrendingUp className="w-4 h-4 text-muted-foreground" />
            </div>
            <p className={`font-heading text-3xl font-bold ${getScoreColor(personal.best_score)}`}>
              {personal.best_score}
            </p>
          </CardContent>
        </Card>

        <Card data-testid="improvement-card">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-muted-foreground">Improvement</span>
              {personal.improvement > 0 ? (
                <ArrowUpRight className="w-4 h-4 text-green-500" />
              ) : personal.improvement < 0 ? (
                <ArrowDownRight className="w-4 h-4 text-red-500" />
              ) : (
                <Minus className="w-4 h-4 text-muted-foreground" />
              )}
            </div>
            <p className={`font-heading text-3xl font-bold ${
              personal.improvement > 0 ? "text-green-500" :
              personal.improvement < 0 ? "text-red-500" : "text-muted-foreground"
            }`}>
              {personal.improvement > 0 ? "+" : ""}{personal.improvement}
            </p>
            <p className="text-xs text-muted-foreground mt-1">Last 5 vs First 5 sessions</p>
          </CardContent>
        </Card>
      </div>

      {/* Score Trend */}
      {personal.score_trend?.length > 0 && (
        <Card data-testid="score-trend-card">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-primary" /> Score Trend
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-end gap-1 h-40">
              {personal.score_trend.map((day, i) => (
                <div key={i} className="flex-1 flex flex-col items-center gap-1 group relative">
                  <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-card border border-border rounded px-2 py-1 text-xs font-mono opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10 shadow-md">
                    {day.date}: {day.avg_score} avg ({day.sessions} sessions)
                  </div>
                  <div
                    className="w-full rounded-t bg-primary/80 hover:bg-primary transition-colors min-h-[4px]"
                    style={{ height: `${(day.avg_score / 100) * 100}%` }}
                  />
                </div>
              ))}
            </div>
            <div className="flex justify-between text-xs text-muted-foreground mt-2">
              <span>{personal.score_trend[0]?.date}</span>
              <span>{personal.score_trend[personal.score_trend.length - 1]?.date}</span>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid md:grid-cols-2 gap-6">
        {/* Score Distribution */}
        {personal.score_distribution && (
          <Card data-testid="score-distribution-card">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-primary" /> Score Distribution
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {Object.entries(personal.score_distribution).map(([bucket, count]) => {
                const total = Object.values(personal.score_distribution).reduce((a, b) => a + b, 0);
                const pct = total > 0 ? (count / total) * 100 : 0;
                return (
                  <div key={bucket} className="space-y-1">
                    <div className="flex justify-between text-sm">
                      <span className="font-medium">{bucket}</span>
                      <span className="text-muted-foreground">{count} ({Math.round(pct)}%)</span>
                    </div>
                    <Progress value={pct} className="h-2" />
                  </div>
                );
              })}
            </CardContent>
          </Card>
        )}

        {/* Scenario Breakdown */}
        {personal.scenario_breakdown?.length > 0 && (
          <Card data-testid="scenario-breakdown-card">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Target className="w-5 h-5 text-primary" /> Most Practiced
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {personal.scenario_breakdown.map((s, i) => (
                <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-muted/30">
                  <div className="min-w-0">
                    <p className="font-medium text-sm truncate">{s.name}</p>
                    <p className="text-xs text-muted-foreground">{s.count} sessions</p>
                  </div>
                  <Badge variant="outline" className={`font-mono ${getScoreColor(s.avg_score)}`}>
                    {s.avg_score}
                  </Badge>
                </div>
              ))}
            </CardContent>
          </Card>
        )}
      </div>

      {personal.total_sessions === 0 && (
        <Card className="p-12 text-center">
          <BarChart3 className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
          <h3 className="font-heading text-xl font-semibold mb-2">No data yet</h3>
          <p className="text-muted-foreground">Complete roleplay sessions to see your analytics</p>
        </Card>
      )}
    </>
  );
}

function TeamStats({ team, getScoreColor }) {
  if (!team) return null;

  return (
    <>
      {/* Team KPI Cards */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card data-testid="team-members-card">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-muted-foreground">Team Members</span>
              <Users className="w-4 h-4 text-muted-foreground" />
            </div>
            <p className="font-heading text-3xl font-bold">{team.total_members}</p>
            <p className="text-xs text-muted-foreground mt-1">{team.active_members} active</p>
          </CardContent>
        </Card>

        <Card data-testid="team-sessions-card">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-muted-foreground">Total Sessions</span>
              <Activity className="w-4 h-4 text-muted-foreground" />
            </div>
            <p className="font-heading text-3xl font-bold">{team.total_sessions}</p>
          </CardContent>
        </Card>

        <Card data-testid="team-avg-score-card">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-muted-foreground">Team Avg Score</span>
              <Target className="w-4 h-4 text-muted-foreground" />
            </div>
            <p className={`font-heading text-3xl font-bold ${getScoreColor(team.avg_score)}`}>
              {team.avg_score}
            </p>
          </CardContent>
        </Card>

        <Card data-testid="team-participation-card">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-muted-foreground">Participation</span>
              <TrendingUp className="w-4 h-4 text-muted-foreground" />
            </div>
            <p className="font-heading text-3xl font-bold">
              {team.total_members > 0 ? Math.round((team.active_members / team.total_members) * 100) : 0}%
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Team Score Trend */}
      {team.score_trend?.length > 0 && (
        <Card data-testid="team-trend-card">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-primary" /> Team Score Trend
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-end gap-1 h-40">
              {team.score_trend.map((day, i) => (
                <div key={i} className="flex-1 flex flex-col items-center gap-1 group relative">
                  <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-card border border-border rounded px-2 py-1 text-xs font-mono opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10 shadow-md">
                    {day.date}: {day.avg_score} avg ({day.sessions} sessions)
                  </div>
                  <div
                    className="w-full rounded-t bg-primary/80 hover:bg-primary transition-colors min-h-[4px]"
                    style={{ height: `${(day.avg_score / 100) * 100}%` }}
                  />
                </div>
              ))}
            </div>
            <div className="flex justify-between text-xs text-muted-foreground mt-2">
              <span>{team.score_trend[0]?.date}</span>
              <span>{team.score_trend[team.score_trend.length - 1]?.date}</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Member Performance */}
      {team.members?.length > 0 && (
        <Card data-testid="member-performance-card">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Users className="w-5 h-5 text-primary" /> Member Performance
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y divide-border">
              {team.members.map((member, i) => (
                <div key={i} className="flex items-center gap-4 px-6 py-3 hover:bg-muted/30 transition-colors">
                  <span className="w-6 font-mono text-sm text-muted-foreground">{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{member.name}</p>
                    <p className="text-xs text-muted-foreground">{member.sessions} sessions</p>
                  </div>
                  <div className="text-right">
                    <p className={`font-mono text-xl font-bold ${getScoreColor(member.avg_score)}`}>
                      {member.avg_score}
                    </p>
                    <p className="text-xs text-muted-foreground">avg</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {team.total_sessions === 0 && (
        <Card className="p-12 text-center">
          <Users className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
          <h3 className="font-heading text-xl font-semibold mb-2">No team data yet</h3>
          <p className="text-muted-foreground">Your team needs to complete sessions to see analytics</p>
        </Card>
      )}
    </>
  );
}
